/* GOODLUUK shared storefront UI infrastructure.
 * Single owner for custom overlay scroll locking, focus trapping and native cart opening.
 */
(function () {
  'use strict';

  if (window.GoodluukUI) return;

  const lockOwners = new Set();
  let savedScrollY = 0;
  let bodySnapshot = null;

  function applyScrollLock() {
    if (lockOwners.size !== 1) return;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    bodySnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow
    };
    document.documentElement.classList.add('gl-overlay-locked');
    document.body.classList.add('gl-overlay-locked');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function releaseScrollLock() {
    if (lockOwners.size || !bodySnapshot) return;
    document.documentElement.classList.remove('gl-overlay-locked');
    document.body.classList.remove('gl-overlay-locked');
    document.documentElement.style.overflow = bodySnapshot.htmlOverflow;
    document.body.style.position = bodySnapshot.position;
    document.body.style.top = bodySnapshot.top;
    document.body.style.left = bodySnapshot.left;
    document.body.style.right = bodySnapshot.right;
    document.body.style.width = bodySnapshot.width;
    document.body.style.overflow = bodySnapshot.overflow;
    bodySnapshot = null;
    window.scrollTo(0, savedScrollY);
  }

  const scrollLock = {
    lock(owner) {
      const key = String(owner || 'anonymous');
      if (lockOwners.has(key)) return;
      lockOwners.add(key);
      applyScrollLock();
    },
    unlock(owner) {
      const key = String(owner || 'anonymous');
      lockOwners.delete(key);
      releaseScrollLock();
    },
    has(owner) {
      return lockOwners.has(String(owner || 'anonymous'));
    }
  };

  const FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function createFocusTrap(container, options) {
    if (!container) return function () {};
    const opts = options || {};
    const returnFocus = opts.returnFocus || document.activeElement;

    function visibleFocusable() {
      return Array.from(container.querySelectorAll(FOCUSABLE)).filter((node) => {
        const style = window.getComputedStyle(node);
        return style.visibility !== 'hidden' && style.display !== 'none' && !node.hasAttribute('hidden');
      });
    }

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = visibleFocusable();
      if (!focusable.length) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    requestAnimationFrame(() => {
      const target = opts.initialFocus || visibleFocusable()[0] || container;
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      target?.focus?.({ preventScroll: true });
    });

    return function releaseFocusTrap() {
      document.removeEventListener('keydown', onKeydown);
      if (opts.restoreFocus !== false && returnFocus && document.contains(returnFocus)) {
        returnFocus.focus?.({ preventScroll: true });
      }
    };
  }

  function openNativeCart(options) {
    const opts = options || {};
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) {
      if (opts.fallback !== false) window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      return;
    }

    if (opts.refresh === false) {
      drawer.show?.();
      return;
    }

    let opened = false;
    const open = function () {
      if (opened) return;
      opened = true;
      drawer.show?.();
    };

    drawer.addEventListener('cart-drawer:refreshed', open, { once: true });
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.setTimeout(open, 700);
  }

  window.GoodluukUI = {
    scrollLock,
    createFocusTrap,
    openNativeCart
  };
})();
